import { memo, useEffect, useState } from 'react'
import { Box, Divider, Grid, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomLabel from 'components/common/CustomLabel/CustomLabel'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import CustomDialog from 'components/dialogs/CustomDialog'
import { useUpdateProductUsages } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'

interface ProductUsage {
  name: { en: string; ar: string };
  instructions: { en: string[]; ar: string[] };
}

const emptyUsage = (): ProductUsage => ({
  name: { en: '', ar: '' },
  instructions: { en: [''], ar: [''] },
});

// A usage is a name and a numbered list, so it is edited as one. It was a JSON
// box before, which is the right editor for a shape nobody has a screen for and
// the wrong one for a procedure somebody reads line by line against the pack.
const fromProduct = (usages: any): ProductUsage[] => {
  if (!Array.isArray(usages)) return [];

  return usages.map((usage: any) => {
    const en: string[] = usage?.instructions?.en ?? [];
    const ar: string[] = usage?.instructions?.ar ?? [];
    // Line for line: the two languages are the same procedure, so a missing
    // Arabic line is an empty box beside its English rather than a shorter list.
    const length = Math.max(en.length, ar.length);

    return {
      name: { en: usage?.name?.en ?? '', ar: usage?.name?.ar ?? '' },
      instructions: {
        en: Array.from({ length }, (_value, index) => en[index] ?? ''),
        ar: Array.from({ length }, (_value, index) => ar[index] ?? ''),
      },
    };
  });
};

function UsagesDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [usages, setUsages] = useState<ProductUsage[]>([]);
  // Which usage is being removed. A usage is a whole procedure - a dozen lines
  // in two languages - so it is worth asking before it goes.
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  // Folded away until asked for, like the other long sections on this page.
  const [isVisible, setIsVisible] = useState<boolean>(false);
  // Which line, and of which usage. Asked for the same reason: the line goes in
  // both languages at once, and the Arabic half is not on screen for whoever is
  // reading the English.
  const [removingLine, setRemovingLine] = useState<{ usage: number; line: number } | null>(null);

  useEffect(() => {
    setUsages(fromProduct(product.usages));
  }, [product.usages]);

  const { mutate: updateMutation, isLoading } = useUpdateProductUsages(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product usages has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the usages', 'error');
    },
  )

  const editUsage = (index: number, change: (usage: ProductUsage) => ProductUsage) => {
    setUsages((previous) => previous.map((usage, i) => (i === index ? change(usage) : usage)));
  }

  const setName = (index: number, language: 'en' | 'ar', value: string) =>
    editUsage(index, (usage) => ({ ...usage, name: { ...usage.name, [language]: value } }));

  const setLine = (index: number, language: 'en' | 'ar', line: number, value: string) =>
    editUsage(index, (usage) => ({
      ...usage,
      instructions: {
        ...usage.instructions,
        [language]: usage.instructions[language].map((text, i) => (i === line ? value : text)),
      },
    }));

  // Added and removed on both sides at once, because a step is one line of a
  // procedure written twice - not two lists that happen to be the same length.
  const addLine = (index: number) =>
    editUsage(index, (usage) => ({
      ...usage,
      instructions: {
        en: [...usage.instructions.en, ''],
        ar: [...usage.instructions.ar, ''],
      },
    }));

  const removeLine = (index: number, line: number) =>
    editUsage(index, (usage) => ({
      ...usage,
      instructions: {
        en: usage.instructions.en.filter((_text, i) => i !== line),
        ar: usage.instructions.ar.filter((_text, i) => i !== line),
      },
    }));

  const handleSave = () => {
    const cleaned = usages.map((usage) => {
      // A line blank in both languages is one nobody wrote, so it is not stored;
      // blank on one side only is kept, because the pair is the unit.
      const keep = usage.instructions.en
        .map((_text, index) => index)
        .filter((index) =>
          !!usage.instructions.en[index]?.trim() || !!usage.instructions.ar[index]?.trim(),
        );

      return {
        name: { en: usage.name.en.trim(), ar: usage.name.ar.trim() },
        instructions: {
          en: keep.map((index) => usage.instructions.en[index]?.trim() ?? ''),
          ar: keep.map((index) => usage.instructions.ar[index]?.trim() ?? ''),
        },
      };
    });

    updateMutation({ id: product.id, usages: cleaned as any });
  }

  const handleCancel = () => {
    setUsages(fromProduct(product.usages));
    setIsEditing(false);
  }

  return (
    <Box mt={3}>
      <Section
        title="Product Usages"
        subtitle={usages.length ? `${usages.length} ${usages.length === 1 ? 'usage' : 'usages'}` : undefined}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => {
                  setIsVisible(true);
                  setIsEditing(true);
                },
                disabled: isLoading,
              }
            ]),
        ]}
        collapsed={!isVisible}
        onToggle={isEditing ? undefined : () => setIsVisible((prev) => !prev)}
        content={isVisible &&
          <Grid size={{ xs: 12 }}>
            {usages.length === 0 && !isEditing && (
              <CustomText text={'No usages yet.'} fontSize={14} color={colors.gray} />
            )}

            <Stack gap={2.5}>
              {usages.map((usage, index) => (
                <Box
                  key={index}
                  sx={{
                    border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <CustomText text={`Usage ${index + 1}`} fontWeight={600} />

                    {isEditing && (
                      <Tooltip title="Remove this usage">
                        <IconButton
                          color="error"
                          onClick={() => setRemovingIndex(index)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <CustomLabel label={'English Name'} />
                      <TextField
                        size="small"
                        fullWidth
                        value={usage.name.en}
                        disabled={!isEditing}
                        onChange={(event) => setName(index, 'en', event.target.value)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <CustomLabel label={'Arabic Name'} />
                      <TextField
                        size="small"
                        fullWidth
                        value={usage.name.ar}
                        disabled={!isEditing}
                        onChange={(event) => setName(index, 'ar', event.target.value)}
                        sx={{ '& input': { direction: 'rtl' } }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <CustomText text={'Instructions'} fontWeight={600} fontSize={15} />

                  <Stack gap={1.5} mt={1.5}>
                    {usage.instructions.en.map((line, lineIndex) => (
                      <Stack key={lineIndex} direction="row" alignItems="flex-start" gap={1}>
                        <Box sx={{ pt: 1.25, minWidth: 24 }}>
                          <CustomText
                            text={`${lineIndex + 1}.`}
                            fontSize={14}
                            color={colors.gray}
                          />
                        </Box>

                        <Grid container spacing={1.5} flex={1}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              value={line}
                              disabled={!isEditing}
                              onChange={(event) =>
                                setLine(index, 'en', lineIndex, event.target.value)
                              }
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              value={usage.instructions.ar[lineIndex] ?? ''}
                              disabled={!isEditing}
                              onChange={(event) =>
                                setLine(index, 'ar', lineIndex, event.target.value)
                              }
                              sx={{ '& textarea': { direction: 'rtl' } }}
                            />
                          </Grid>
                        </Grid>

                        {isEditing && (
                          <Box sx={{ pt: 0.5 }}>
                            <Tooltip title="Remove this line">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setRemovingLine({ usage: index, line: lineIndex })}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Stack>
                    ))}
                  </Stack>

                  {isEditing && (
                    <Box mt={2}>
                      <CustomButton
                        text={'Add Line'}
                        variant={'outlined'}
                        startIcon={<AddIcon />}
                        height={38}
                        onClick={() => addLine(index)}
                      />
                    </Box>
                  )}
                </Box>
              ))}

              {isEditing && (
                <CustomButton
                  text={'Add Usage'}
                  variant={'outlined'}
                  startIcon={<AddIcon />}
                  height={40}
                  fullWidth
                  onClick={() => setUsages((previous) => [...previous, emptyUsage()])}
                />
              )}
            </Stack>
          </Grid>
        }
      />

      <CustomDialog
        open={removingLine !== null}
        title={'Remove Line'}
        message={'This removes the line in both languages. It is only stored once you save.'}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant={'outlined'}
            text={'Cancel'}
            textColor={'error'}
            height={35}
            onClick={() => setRemovingLine(null)}
          />,
          <CustomButton
            key={'remove'}
            variant={'filled'}
            color={'error'}
            text={'Remove'}
            height={35}
            onClick={() => {
              if (removingLine) removeLine(removingLine.usage, removingLine.line);
              setRemovingLine(null);
            }}
          />,
        ]}
      />

      <CustomDialog
        open={removingIndex !== null}
        title={'Remove Usage'}
        message={'This removes the whole procedure and its lines in both languages. It is only stored once you save.'}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant={'outlined'}
            text={'Cancel'}
            textColor={'error'}
            height={35}
            onClick={() => setRemovingIndex(null)}
          />,
          <CustomButton
            key={'remove'}
            variant={'filled'}
            color={'error'}
            text={'Remove'}
            height={35}
            onClick={() => {
              setUsages((previous) => previous.filter((_item, i) => i !== removingIndex));
              setRemovingIndex(null);
            }}
          />,
        ]}
      />
    </Box>
  )
}

export default memo(UsagesDetails)
